import { Fragment, useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';

import ShareIcon from './ShareIcon';
import HomeScreenIcon from './HomeScreenIcon';

import './PWAPrompt.styles.scss';
import { useTranslation } from 'react-i18next';
import { RESOURCES_KEYS } from 'utils/i18n/resources';

type PWAPromptProps = {
  delay: number;
  maxVisits: number;
  permanentlyHideOnDismiss: boolean;
  promptData: { isIos: boolean; visits: number };
};
const PWAPrompt: React.FunctionComponent<PWAPromptProps> = ({
  permanentlyHideOnDismiss,
  promptData,
  maxVisits,
  delay,
}) => {
  const [isVisible, setVisibility] = useState(!Boolean(delay));
  const { t: translate } = useTranslation();

  useEffect(() => {
    if (delay) {
      setTimeout(() => {
        // Prevent keyboard appearing over the prompt if a text input has autofocus set
        if (document.activeElement) {
          (document.activeElement as any).blur();
        }

        setVisibility(true);
      }, delay);
    }
  }, [delay]);

  useEffect(() => {
    if (isVisible) {
      document.body.classList.add('noScroll');
    }
  }, [isVisible]);

  const isiOS13AndUp = /OS (13|14)/.test(window.navigator.userAgent);
  const visibilityClass = isVisible ? 'visible' : 'hidden';
  const iOSClass = isiOS13AndUp ? 'modern' : 'legacy';

  const dismissPrompt = (evt: any) => {
    document.body.classList.remove('noScroll');
    setVisibility(false);
  };

  const applyPrompt = (evt: any) => {
    document.body.classList.remove('noScroll');
    setVisibility(false);

    if (permanentlyHideOnDismiss) {
      localStorage.setItem(
        'iosPwaPrompt',
        JSON.stringify({
          ...promptData,
          visits: maxVisits,
        })
      );
    }
  };

  const onTransitionOut = (evt: any) => {
    if (!isVisible) {
      evt.currentTarget.style.display = 'none';
    }
  };

  return (
    <Fragment>
      <div
        className={`${'pwaPromptOverlay'} ${visibilityClass} ${iOSClass} iOSPWA-overlay`}
        aria-label='Close'
        role='button'
        onClick={dismissPrompt}
        onTransitionEnd={onTransitionOut}
      />
      <div
        className={`${'pwaPrompt'} ${visibilityClass} ${iOSClass} iOSPWA-container`}
        aria-describedby='pwa-prompt-description'
        aria-labelledby='pwa-prompt-title'
        role='dialog'
        onTransitionEnd={onTransitionOut}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }} className={`${'pwaPromptHeader'} iOSPWA-header`}>
          <Typography id='pwa-prompt-title' className={`${'pwaPromptTitle'} iOSPWA-title`}>
            {translate(RESOURCES_KEYS.IOS_PROMPT_ADD_TO_HOME_SCREEN)}
          </Typography>
          <Button color='error' className={`${'pwaPromptCancel'} iOSPWA-cancel`} onClick={dismissPrompt}>
            <b>X</b>
          </Button>
        </Box>
        <div className={`${'pwaPromptBody'} iOSPWA-body`}>
          <div className={`${'pwaPromptDescription'} iOSPWA-description`}>
            <p id='pwa-prompt-description' className={`${'pwaPromptCopy'} iOSPWA-description-copy`}>
              {translate(RESOURCES_KEYS.IOS_PROMPT_ADD_TO_HOME_SCREEN_MESSAGE)}
            </p>
          </div>
        </div>
        <div className={`${'pwaPromptInstruction'} iOSPWA-steps`}>
          <div className={`${'pwaPromptInstructionStep'} iOSPWA-step1`}>
            <ShareIcon className={`${'pwaPromptShareIcon'} iOSPWA-step1-icon`} modern={isiOS13AndUp} />
            <p className={`${'pwaPromptCopy'} ${'bold'} iOSPWA-step1-copy`}>
              {translate(RESOURCES_KEYS.IOS_PROMPT_PRESS_SHARE_BUTTON)}
            </p>
          </div>
          <div className={`${'pwaPromptInstructionStep'} iOSPWA-step2`}>
            <HomeScreenIcon className={`${'pwaPromptHomeIcon'} iOSPWA-step2-icon`} modern={isiOS13AndUp} />
            <p className={`${'pwaPromptCopy'} ${'bold'} iOSPWA-step2-copy`}>
              {translate(RESOURCES_KEYS.IOS_PROMPT_PRESS_ADD_TO_HOME_SCREEN_BUTTON)}
            </p>
          </div>
        </div>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: '13px 16px' }}>
          <Button variant='outlined' className={`${'pwaPromptCancel'} iOSPWA-cancel`} onClick={applyPrompt}>
            Ok
          </Button>
        </Box>
      </div>
    </Fragment>
  );
};

export default PWAPrompt;
